import React, { useState } from 'react';
import { Lock, Crown, Zap } from 'lucide-react';
import { useSubscription } from '../../Contexts/SubscriptionContext';
import PricingModal from './PricingModal';

const FeatureGate = ({ 
  feature, 
  children, 
  hasAccessOverride,
  fallback = null,
  showUpgradePrompt = true,
  featureName = 'this feature',
  featureDescription = 'Upgrade to Premium to unlock this feature.'
}) => {
  const { checkFeatureAccess, subscription } = useSubscription();
  const [showPricing, setShowPricing] = useState(false);
  const trialEndDate = subscription?.trialEndDate
    ? (subscription.trialEndDate.toDate ? subscription.trialEndDate.toDate() : new Date(subscription.trialEndDate))
    : null;
  const isTrialActive = subscription?.subscriptionStatus === 'trial' && trialEndDate && trialEndDate > new Date();
  const hasUsedTrial = !isTrialActive && (
    subscription?.subscriptionStatus === 'expired' ||
    subscription?.trialUsed ||
    localStorage.getItem('trial_used') === 'true'
  );
  
  const hasAccess =
    typeof hasAccessOverride === 'boolean'
      ? hasAccessOverride
      : checkFeatureAccess(feature);

  // If user has access, render the children
  if (hasAccess) {
    return children;
  }

  // If fallback is provided, render it
  if (fallback) {
    return fallback;
  }

  // If showUpgradePrompt is false, render nothing
  if (!showUpgradePrompt) {
    return null;
  }

  // Default upgrade prompt
  return (
    <>
      <div className="relative">
        {/* Blurred/disabled content */}
        <div className="filter blur-sm opacity-50 pointer-events-none">
          {children}
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg">
          <div className="text-center p-6 max-w-sm">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Premium Feature
            </h3>
            
            <p className="text-gray-600 text-sm mb-4">
              {featureDescription}
            </p>
            
            <button
              onClick={() => setShowPricing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#4FB368] text-white rounded-lg font-medium hover:bg-opacity-90 transition-colors"
            >
              <Zap className="w-4 h-4" />
              Start Free Trial
            </button>
            
            <p className="text-xs text-gray-500 mt-2">
              14 days free, then €15/month (VAT incl.)
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        onStartTrial={() => {
          // This will be handled by the parent component
          setShowPricing(false);
        }}
        userSubscription={subscription}
        hasUsedTrial={hasUsedTrial}
      />
    </>
  );
};

// Specific feature gates for common features
export const VoiceToTextGate = ({ children, ...props }) => (
  <FeatureGate
    feature="voice_to_text"
    featureName="Voice-to-Text"
    featureDescription="Speak your expenses and let AI convert them to structured data automatically."
    {...props}
  >
    {children}
  </FeatureGate>
);

export const ReceiptScannerGate = ({ children, ...props }) => (
  <FeatureGate
    feature="receipt_scanner"
    featureName="Receipt Scanner"
    featureDescription="Scan receipts with your camera and extract all transaction details automatically."
    {...props}
  >
    {children}
  </FeatureGate>
);

export const AdvancedReportsGate = ({ children, ...props }) => (
  <FeatureGate
    feature="advanced_reports"
    featureName="Advanced Reports"
    featureDescription="Generate detailed financial reports and analytics for your business."
    {...props}
  >
    {children}
  </FeatureGate>
);

export default FeatureGate;
